/**
 * Rendre une provision, quand il n'y a aucune note de crédit à citer.
 *
 * ## Le cas
 *
 * Signalé le 20 septembre 2026 : « la maman a payé deux fois, du coup pas de
 * note de crédit à lier mais juste le remboursement de la provision ».
 *
 * C'est exact, et c'est structurel. Un double paiement ne produit aucune note
 * de crédit : la facture est due une fois et payée une fois, le second
 * versement va en **provision**, et le virement qui le rend s'impute à cette
 * provision. L'écran ne proposait que `target: 'credit_note'` et affichait
 * « Aucune note remboursable ne correspond », avec pour seule issue « Classer
 * hors périmètre familles » — ce qui aurait été faux : c'est de l'argent de
 * famille.
 *
 * ## Ce que la mesure a montré
 *
 * Sur les virements sortants encore en file, **29 appartiennent à une famille
 * qui a une provision**, pour 2 221,00 €. Cinq se relient à une facture qui n'a
 * **aucune** note de crédit remboursable — c'est le cas exact du signalement.
 * Les quatre configurations possibles existent toutes en production :
 *
 * | virement | provision | ce qu'il faut dire |
 * |---|---|---|
 * | 90,00 € `CDV-250530-00010` | 90,00 € | elle tombe juste |
 * | 180,00 € `CDV-250602-00070` | 300,00 € | il en restera 120,00 € |
 * | 60,00 € `CDV-250530-00002` | 0,00 € | il n'y a aucune provision |
 * | 49,00 € `CDV-WBK-2601307` | 0,00 € | idem |
 *
 * ## `avoir_cents`, jamais `available_cents`
 *
 * `v_user_provision_balances` rend les deux, et ils ne disent pas la même
 * chose. `available_cents` est ce qu'on peut CONSOMMER maintenant, et il est
 * gardé par le réglage `provision_auto` — qui vaut `off`. Il vaut donc **zéro
 * pour les 149 familles qui ont un avoir**, soit 9 443,45 € invisibles.
 *
 * Lire `available_cents` ici afficherait « aucune provision » devant une
 * provision qui existe, et ferait écarter des virements parfaitement
 * rapprochables. C'est `avoir_cents` — ce que la famille POSSÈDE — qui compte.
 *
 * ## Rien ne bloque
 *
 * Aucun plafond n'existe en base sur une imputation à la provision : les
 * déclencheurs ne vérifient que l'invariant 1' (la somme des imputations ne
 * dépasse pas le virement). On PEUT donc rendre plus que la provision, et
 * c'est délibéré — la règle posée le 19 septembre vaut ici aussi : « le
 * virement est parti en banque, l'administration doit pouvoir le rapprocher ».
 *
 * Ce module ne refuse donc jamais. Il DIT ce qui ne concorde pas, et laisse
 * décider.
 */

/** Une famille à qui ce virement peut rendre une provision. */
export interface ProvisionDeFamille {
  userId: string;
  famille: string;
  /** Ce que la famille possède. Voir plus haut : jamais `available_cents`. */
  avoirCents: number;
  /** La facture citée qui a mené jusqu'à elle. */
  parLaFacture: string | null;
  /** Ce qui a constitué la provision, tel que l'imputation l'a noté. */
  origine: string | null;
}

/** Comment le virement et la provision se répondent. */
export type AccordDeLaProvision =
  /** Le virement rend exactement la provision. */
  | 'exact'
  /** Il en rend une partie ; le reste demeure au crédit de la famille. */
  | 'reste'
  /** La provision est plus petite que le virement. */
  | 'insuffisante'
  /** Cette famille n'a aucune provision. */
  | 'aucune';

export interface ProvisionARendre {
  famille: ProvisionDeFamille;
  /** Ce que le bouton écrira, avant toute correction à la main. */
  proposeCents: number;
  accord: AccordDeLaProvision;
  /** L'état, en une phrase, telle qu'un écran peut l'afficher sans rien ajouter. */
  phrase: string;
  /** Vrai quand la situation mérite d'être regardée avant d'écrire. */
  aVerifier: boolean;
}

export function lireLaProvisionARendre(
  aPlacerCents: number,
  famille: ProvisionDeFamille,
): ProvisionARendre {
  const avoir = Math.max(0, famille.avoirCents);

  /*
    Quand la provision existe, on ne propose jamais plus qu'elle : le reste du
    virement n'est pas un remboursement de provision, et le laisser à placer
    est plus juste que de l'y fondre. Quand elle n'existe pas, il n'y a rien de
    mieux à proposer que le montant entier — accompagné de la phrase qui dit
    que rien ne l'appuie.
  */
  const propose = avoir > 0 ? Math.min(aPlacerCents, avoir) : aPlacerCents;

  let accord: AccordDeLaProvision;
  if (avoir === 0) accord = 'aucune';
  else if (avoir === aPlacerCents) accord = 'exact';
  else if (avoir > aPlacerCents) accord = 'reste';
  else accord = 'insuffisante';

  return {
    famille,
    proposeCents: propose,
    accord,
    phrase: phrase(aPlacerCents, avoir, accord, famille),
    aVerifier: accord === 'aucune' || accord === 'insuffisante',
  };
}

function phrase(
  aPlacerCents: number,
  avoir: number,
  accord: AccordDeLaProvision,
  famille: ProvisionDeFamille,
): string {
  const nom = famille.famille;
  const origine = famille.origine ? ` (${famille.origine})` : '';

  switch (accord) {
    case 'exact':
      return `${nom} a ${euros(avoir)} en provision${origine}, et ce virement les rend exactement.`;
    case 'reste':
      return `${nom} a ${euros(avoir)} en provision${origine}. Ce virement en rend ${euros(aPlacerCents)} ; `
        + `il restera ${euros(avoir - aPlacerCents)} au crédit de la famille.`;
    case 'insuffisante':
      return `${nom} n'a que ${euros(avoir)} en provision${origine}, et ce virement vaut ${euros(aPlacerCents)}. `
        + `Les ${euros(aPlacerCents - avoir)} de différence ne sont pas un remboursement de provision : à vérifier.`;
    case 'aucune':
      return `${nom} n'a aucune provision. Le virement est pourtant parti en banque : `
        + `on peut l'imputer quand même, mais rien ne l'appuie ici — à vérifier.`;
  }
}

/** Les familles qu'un virement peut rembourser, la plus dotée d'abord. */
export function provisionsARendre(
  aPlacerCents: number,
  familles: ProvisionDeFamille[],
): ProvisionARendre[] {
  const parFamille = new Map<string, ProvisionDeFamille>();
  for (const f of familles) if (!parFamille.has(f.userId)) parFamille.set(f.userId, f);

  return [...parFamille.values()]
    .map((f) => lireLaProvisionARendre(aPlacerCents, f))
    /* Celle dont la provision explique le mieux ce virement, en tête. */
    .sort((a, b) => b.famille.avoirCents - a.famille.avoirCents);
}

const EUR = new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' });
function euros(cents: number): string {
  return EUR.format(cents / 100);
}
